<template>
  <header :class="['header', { blue: azul }]">
    <h1>{{ nombre }}</h1>
    <div class="header-right">
      <!-- Antecedentes chip + panel -->
      <div class="ant-wrapper">
        <button class="ant-chip" @click.stop="togglePanel">
          Antecedentes
          <span v-if="tieneAlgo" class="ant-dot" :style="{ background: dotColor }"></span>
        </button>
        <div v-if="panelAbierto" class="ant-panel" @click.stop>
          <div class="ant-campo" @click="abrirModal">
            <span class="ant-label">Alergias</span>
            <span
              class="ant-valor"
              :class="{ empty: !alergias, 'ant-valor-chip': alertaAlergias && alergias }"
              :style="alertaAlergias && alergias ? { background: alertaAlergias } : {}"
            >{{ alergias ? truncar(alergias) : 'Sin registrar' }}</span>
            <span v-if="alergias && alergias.length > LIMIT" class="ver-todo">ver todo →</span>
          </div>
          <div class="ant-campo" @click="abrirModal">
            <span class="ant-label">Enfermedades</span>
            <span
              class="ant-valor"
              :class="{ empty: !enfermedades, 'ant-valor-chip': alertaEnfermedades && enfermedades }"
              :style="alertaEnfermedades && enfermedades ? { background: alertaEnfermedades } : {}"
            >{{ enfermedades ? truncar(enfermedades) : 'Sin registrar' }}</span>
            <span v-if="enfermedades && enfermedades.length > LIMIT" class="ver-todo">ver todo →</span>
          </div>
          <div class="ant-campo" @click="abrirModal">
            <span class="ant-label">Observaciones</span>
            <span
              class="ant-valor"
              :class="{ empty: !observaciones, 'ant-valor-chip': alertaObservaciones && observaciones }"
              :style="alertaObservaciones && observaciones ? { background: alertaObservaciones } : {}"
            >{{ observaciones ? truncar(observaciones) : 'Sin registrar' }}</span>
            <span v-if="observaciones && observaciones.length > LIMIT" class="ver-todo">ver todo →</span>
          </div>
          <button class="ant-editar-btn" @click="abrirModal">Editar</button>
        </div>
      </div>

      <!-- Foto -->
      <div class="image-container">
        <img :src="imageSrc" @click="$emit('ver-imagen')" />
        <button class="upload-btn" @click="triggerUpload">+</button>
        <input ref="fileInput" id="foto-paciente" name="foto-paciente" type="file" style="display:none" @change="handleFile" accept="image/*" />
      </div>
    </div>
  </header>

  <ModalAntecedentes
    :visible="modalAbierto"
    :alergias="alergias"
    :enfermedades="enfermedades"
    :observaciones="observaciones"
    :alertaAlergias="alertaAlergias"
    :alertaEnfermedades="alertaEnfermedades"
    :alertaObservaciones="alertaObservaciones"
    @cerrar="modalAbierto = false"
    @guardar="onGuardar"
  />

  <ModalPagoPendiente :mostrar="mostrarModalPago" @cerrar="cerrarModalPago" />
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import ModalAntecedentes from '@/components/ModalAntecedentes.vue';
import ModalPagoPendiente from '@/components/ModalPagoPendiente.vue';
import { usePagoVerificacion } from '@/composables/usePagoVerificacion';
import { PLACEHOLDER_PACIENTE } from '@/constants/imagenes';

const props = defineProps({
  nombre: { type: String, required: true },
  azul: { type: Boolean, default: false },
  imageSrc: { type: String, default: PLACEHOLDER_PACIENTE },
  alergias: { type: String, default: '' },
  enfermedades: { type: String, default: '' },
  observaciones: { type: String, default: '' },
  alertaAlergias: { type: String, default: '' },
  alertaEnfermedades: { type: String, default: '' },
  alertaObservaciones: { type: String, default: '' },
});

const alertasActivas = computed(() => {
  const result = [];
  if (props.alertaAlergias && props.alergias) result.push({ texto: 'Alergias', color: props.alertaAlergias });
  if (props.alertaEnfermedades && props.enfermedades) result.push({ texto: 'Enfermedades', color: props.alertaEnfermedades });
  if (props.alertaObservaciones && props.observaciones) result.push({ texto: 'Observaciones', color: props.alertaObservaciones });
  return result;
});

const dotColor = computed(() =>
  alertasActivas.value.length ? alertasActivas.value[0].color : '#fbbf24'
);

const emit = defineEmits(['ver-imagen', 'subir-foto', 'guardar-antecedentes']);

const { mostrarModalPago, verificarPermisoPago, cerrarModalPago } = usePagoVerificacion();

const fileInput = ref(null);
const panelAbierto = ref(false);
const modalAbierto = ref(false);

const LIMIT = 80;

const tieneAlgo = computed(() => !!(props.alergias || props.enfermedades || props.observaciones));

function truncar(text) {
  if (!text) return '';
  return text.length > LIMIT ? text.slice(0, LIMIT) + '...' : text;
}

function togglePanel() {
  panelAbierto.value = !panelAbierto.value;
}

function abrirModal() {
  if (!verificarPermisoPago()) return;
  panelAbierto.value = false;
  modalAbierto.value = true;
}

function onGuardar(datos) {
  emit('guardar-antecedentes', datos);
}

function triggerUpload() {
  fileInput.value.click();
}

function handleFile(event) {
  const file = event.target.files[0];
  if (file) {
    emit('subir-foto', file);
  }
}

// Cerrar panel al hacer click fuera
function onClickFuera() {
  panelAbierto.value = false;
}

watch(panelAbierto, (val) => {
  if (val) {
    setTimeout(() => document.addEventListener('click', onClickFuera), 0);
  } else {
    document.removeEventListener('click', onClickFuera);
  }
});
</script>

<style scoped>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}


.header.blue {
  background: #678AB2;
  color: white;
  padding: 0 16px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

h1 {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

/* Antecedentes */
.ant-wrapper {
  position: relative;
}

.ant-chip {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  transition: background 0.15s;
}

.ant-chip:hover {
  background: rgba(255, 255, 255, 0.3);
}

.ant-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 0 2px rgba(255,255,255,0.4);
}

.ant-panel {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 270px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 500;
  overflow: hidden;
}

.ant-campo {
  padding: 11px 14px;
  border-bottom: 1px solid #f1f5f9;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 3px;
  transition: background 0.15s;
}

.ant-campo:hover {
  background: #f8fafc;
}

.ant-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #94a3b8;
}

.ant-valor {
  font-size: 13px;
  color: #1e293b;
  line-height: 1.4;
}

.ant-valor.empty {
  color: #cbd5e1;
  font-style: italic;
}

.ant-valor-chip {
  display: inline-block;
  align-self: flex-start;
  color: white;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 13px;
}

.ver-todo {
  font-size: 11px;
  color: #678AB2;
  font-weight: 600;
}

.ant-editar-btn {
  width: 100%;
  padding: 10px;
  background: #f0f7ff;
  color: #678AB2;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  transition: background 0.15s;
}

.ant-editar-btn:hover {
  background: #dbeafe;
}

/* Foto */
img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
}

.image-container {
  position: relative;
  display: inline-block;
}

.upload-btn {
  position: absolute;
  bottom: -5px;
  right: -5px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #678AB2;
  color: white;
  border: none;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Tablet */
@media (min-width: 768px) {
  .header.blue {
    height: 64px;
    padding: 0 24px;
    margin-bottom: 16px;
  }

  h1 {
    font-size: 24px;
  }

  img {
    width: 48px;
    height: 48px;
  }

  .ant-chip {
    font-size: 16px;
    padding: 12px 20px;
  }
}

/* Desktop */
@media (min-width: 1024px) {
  .header.blue {
    height: 64px;
    padding: 0 32px;
    margin-bottom: 20px;
  }

  h1 {
    font-size: 28px;
  }

  img {
    width: 48px;
    height: 48px;
  }
}
</style>
